import React, { useState, useEffect } from 'react'
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  BuildingOfficeIcon,
  UserIcon,
  CalendarIcon
} from '@heroicons/react/24/outline'
import { ServiceProviderDocument } from '../../types'
import { formatDate } from '../../utils/formatters'
import { Button } from '../ui'
import { API_BASE } from '../../services/api'

const API_BASE_URL = API_BASE

interface DocumentPreviewModalProps {
  document: ServiceProviderDocument | null
  isOpen: boolean
  onClose: () => void
}

export default function DocumentPreviewModal({ document, isOpen, onClose }: DocumentPreviewModalProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && document) {
      loadPreview()
    } else {
      setPreviewUrl(null)
      setError(null)
    }
  }, [isOpen, document])

  const loadPreview = async () => {
    if (!document) return

    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('Authentication token not found')
      }

      const downloadUrl = `${API_BASE_URL}/api/documents/provider/${document.id}/download`

      if (document.mimeType === 'application/pdf') {
        const response = await fetch(downloadUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': document.mimeType
          }
        })
        if (!response.ok) {
          throw new Error(`Failed to fetch document: ${response.status} ${response.statusText}`)
        }
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        setPreviewUrl(url)
        return () => URL.revokeObjectURL(url)
      } else if (document.mimeType.startsWith('image/')) {
        const response = await fetch(downloadUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': document.mimeType
          }
        })
        if (!response.ok) {
          throw new Error(`Failed to fetch document: ${response.status} ${response.statusText}`)
        }
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        setPreviewUrl(url)
        return () => URL.revokeObjectURL(url)
      } else {
        setError('Preview not available for this file type')
      }
    } catch (err) {
      console.error('Error loading document preview:', err)
      setError(`Failed to load document preview: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = async () => {
    if (document) {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          throw new Error('Authentication token not found')
        }

        const response = await fetch(`${API_BASE_URL}/api/documents/provider/${document.id}/download`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (!response.ok) {
          throw new Error(`Failed to download document: ${response.status} ${response.statusText}`)
        }

        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = document.originalName
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Failed to download document:', error)
        const link = document.createElement('a')
        link.href = `${API_BASE_URL}/api/documents/provider/${document.id}/download`
        link.download = document.originalName
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    }
  }

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'UPLOADED':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'VERIFIED':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'REJECTED':
        return 'bg-red-100 text-red-800 border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <ClockIcon className="h-4 w-4" />
      case 'VERIFIED':
        return <CheckCircleIcon className="h-4 w-4" />
      default:
        return <DocumentTextIcon className="h-4 w-4" />
    }
  }

  if (!isOpen || !document) return null

  const isPdf = document.mimeType === 'application/pdf'
  const isImage = document.mimeType.startsWith('image/')

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-6 border-b border-neutral-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary-100 rounded-lg">
              <DocumentTextIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-neutral-900">Document Details</h2>
              <p className="text-sm text-neutral-500 mt-1">
                {document.originalName}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(document.status)}`}>
              {getStatusIcon(document.status)}
              <span className="ml-1">{document.status.replace(/_/g, ' ')}</span>
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="flex items-center gap-2"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Open document in new tab
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="p-2"
            >
              <XMarkIcon className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Provider Info */}
            <div className="bg-neutral-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-neutral-900 mb-3 flex items-center gap-2">
                <BuildingOfficeIcon className="h-4 w-4" />
                Provider Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Provider Name</label>
                  <p className="text-sm text-neutral-900">
                    {document.serviceProvider.companyName ||
                     `${document.serviceProvider.user.firstName} ${document.serviceProvider.user.lastName}`}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Provider ID</label>
                  <p className="text-sm text-neutral-900">{document.serviceProvider.id}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Service Type</label>
                  <p className="text-sm text-neutral-900">
                    {document.serviceProvider.serviceType.replace(/_/g, ' ')}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Document Type</label>
                  <p className="text-sm text-neutral-900">
                    {document.type.replace(/_/g, ' ')}
                  </p>
                </div>
              </div>
            </div>

            {/* Document Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">File Size</label>
                <p className="text-sm text-neutral-900">{(document.fileSize / 1024).toFixed(1)} KB</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">File Type</label>
                <p className="text-sm text-neutral-900">{document.mimeType}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Uploaded Date</label>
                <p className="text-sm text-neutral-900">{formatDate(document.uploadedAt)}</p>
              </div>
              {document.verifiedAt && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Verified Date</label>
                  <p className="text-sm text-neutral-900">{formatDate(document.verifiedAt)}</p>
                </div>
              )}
            </div>

            {/* Verification Notes */}
            {document.verificationNotes && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="text-sm font-medium text-amber-900 mb-2 flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  Verification Notes
                </h4>
                <p className="text-sm text-amber-800">{document.verificationNotes}</p>
              </div>
            )}

            {/* Document Preview */}
            <div className="border border-neutral-200 rounded-lg overflow-hidden">
              <div className="bg-neutral-50 px-4 py-3 border-b border-neutral-200">
                <h3 className="text-sm font-medium text-neutral-900 flex items-center gap-2">
                  <EyeIcon className="h-4 w-4" />
                  Document Preview
                </h3>
              </div>
              <div className="p-4 min-h-[400px] flex items-center justify-center bg-white">
                {loading && (
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
                    <p className="text-neutral-600">Loading preview...</p>
                  </div>
                )}

                {error && (
                  <div className="text-center">
                    <ExclamationTriangleIcon className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                    <p className="text-neutral-600 mb-4">{error}</p>
                    <Button
                      onClick={handleDownload}
                      className="flex items-center gap-2 mx-auto"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      Download Document
                    </Button>
                  </div>
                )}

                {previewUrl && !loading && !error && (
                  <div className="w-full h-full flex items-center justify-center">
                    {isPdf && (
                      <iframe
                        src={previewUrl}
                        className="w-full h-[600px] border border-neutral-200 rounded"
                        title={`PDF preview: ${document.originalName}`}
                      />
                    )}

                    {isImage && (
                      <div className="max-w-full max-h-full flex items-center justify-center">
                        <img
                          src={previewUrl}
                          alt={document.originalName}
                          className="max-w-full max-h-full object-contain rounded shadow-lg"
                        />
                      </div>
                    )}
                  </div>
                )}

                {!previewUrl && !loading && !error && !isPdf && !isImage && (
                  <div className="text-center">
                    <DocumentTextIcon className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
                    <p className="text-neutral-600 mb-4">Preview not available for this file type</p>
                    <Button
                      onClick={handleDownload}
                      className="flex items-center gap-2 mx-auto"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      Download Document
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}